import { useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TenantTypeSchema, slugify, type TenantType } from '@bliks-io/contracts';
import { TENANT_TYPE_LABEL_KEYS } from './TenantTypeBadge';
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn
} from '@bliks-io/ui';

/**
 * Form shape shared by the create/edit and clone tenant dialogs. Both POST an
 * identical tenant-identity body, so they render the same fields.
 */
export interface TenantIdentityFormValues {
  name: string;
  slug: string;
  tenantType: TenantType;
  expiresAt?: string | null;
  description?: string;
  /** The HubSpot company of the customer this tenant belongs to. */
  hubspotCompanyLinkId?: string;
}

/** The HubSpot link a tenant write may carry. */
export interface TenantHubspotLinkFields {
  hubspotCompanyId?: string;
}

/** The id already stored on a tenant, through its account. */
export function hubspotCompanyLinkIdOf(tenant: TenantHubspotLinkFields): string {
  return tenant.hubspotCompanyId ?? '';
}

/**
 * Map the form's HubSpot company id onto the write contract.
 *
 * One key for every tenant type: the id names the CUSTOMER, and the backend
 * resolves it to an account the tenant belongs to (LIC-06). Partner tenants
 * carried a second copy of it until migration 20260827-002 moved it there.
 *
 * A cleared input has to reach the backend as `''` (the clear protocol):
 * `undefined` would be dropped by `JSON.stringify` and silently keep the old
 * id. On create there is nothing to detach, so an empty id omits the key.
 */
export function toHubspotLinkFields(
  values: Pick<TenantIdentityFormValues, 'tenantType' | 'hubspotCompanyLinkId'>,
  mode: 'create' | 'edit'
): TenantHubspotLinkFields {
  const companyId = values.hubspotCompanyLinkId?.trim() ?? '';
  if (mode === 'create') return companyId ? { hubspotCompanyId: companyId } : {};
  return { hubspotCompanyId: companyId };
}

/** `<input type="date">` value (YYYY-MM-DD) for a stored ISO timestamp. */
export function isoToDateInput(iso: string | null | undefined): string | null {
  return iso ? iso.substring(0, 10) : null;
}

/**
 * End-of-day ISO timestamp for a date-input value (YYYY-MM-DD). Anchoring to
 * 23:59:59.999Z keeps an expiry valid through the whole selected day.
 */
export function dateInputToIso(dateInput: string | null | undefined): string | null {
  return dateInput ? new Date(`${dateInput}T23:59:59.999Z`).toISOString() : null;
}

interface TenantIdentityFieldsProps {
  form: UseFormReturn<TenantIdentityFormValues>;
  /** Re-derive the slug from the name on each keystroke (off when editing an existing slug). */
  autoSlug: boolean;
  /**
   * Render the HubSpot company-id field. Only the add/update flow submits it;
   * the clone flow leaves it off rather than showing a control whose input is
   * silently discarded.
   */
  showHubspotLink?: boolean;
}

/**
 * The name / slug / type / expiry fields shared by the tenant create and clone
 * dialogs, laid out two-per-row. Renders inside the dialog's own `space-y-4`
 * wrapper (so full-width siblings like the clone warning stack above it).
 */
export function TenantIdentityFields({
  form,
  autoSlug,
  showHubspotLink = false
}: TenantIdentityFieldsProps) {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    setValue,
    watch
  } = form;
  const tenantType = watch('tenantType');
  const nameRegistration = register('name', {
    required: t('tenantAdmin.tenantNameRequired')
  });
  // A half-typed date reports an EMPTY value with `validity.badInput`, which
  // `setValueAs` would hand on as the clear protocol's `null` — silently wiping
  // a stored expiry. Read the element back at validation time so the save is
  // refused instead; the live element also can't go stale across a form reset.
  const expiresAtElement = useRef<HTMLInputElement | null>(null);
  const expiresAtRegistration = register('expiresAt', {
    setValueAs: (v: string) => v || null,
    validate: () => !expiresAtElement.current?.validity.badInput || t('errors.validation.invalid')
  });

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {/* Tenant Name */}
      <div className="space-y-2">
        <Label htmlFor="name">{t('tenantAdmin.tenantName')}</Label>
        <Input
          id="name"
          type="text"
          placeholder={t('tenantAdmin.tenantNamePlaceholder')}
          {...nameRegistration}
          onChange={(e) => {
            nameRegistration.onChange(e);
            if (autoSlug && e.target.value) {
              setValue('slug', slugify(e.target.value));
            }
          }}
          className={cn(errors.name && 'border-destructive')}
        />
        {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
      </div>

      {/* Tenant Slug */}
      <div className="space-y-2">
        <Label htmlFor="slug">{t('tenantAdmin.tenantSlug')}</Label>
        <Input
          id="slug"
          type="text"
          placeholder={t('tenantAdmin.tenantSlugPlaceholder')}
          {...register('slug', {
            required: t('tenantAdmin.tenantSlugRequired'),
            pattern: {
              value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
              message: t('tenantAdmin.tenantSlugPattern')
            }
          })}
          className={cn(errors.slug && 'border-destructive')}
        />
        {errors.slug && <p className="text-sm text-destructive">{errors.slug.message}</p>}
        <p className="text-sm text-muted-foreground">{t('tenantAdmin.tenantSlugHelper')}</p>
      </div>

      {/* Tenant Type */}
      <div className="space-y-2">
        <Label htmlFor="tenantType">{t('tenantAdmin.tenantType')}</Label>
        <Select
          value={tenantType}
          // `shouldDirty` is load-bearing, not cosmetic: FormDialog closes an
          // edit without submitting when nothing is dirty, and sends only the
          // dirty subset otherwise — a plain setValue would make a type-only
          // change a silent no-op.
          onValueChange={(value) =>
            setValue('tenantType', value as TenantType, { shouldDirty: true })
          }
        >
          <SelectTrigger id="tenantType">
            <SelectValue placeholder={t('tenantAdmin.tenantTypePlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {TenantTypeSchema.options.map((type) => (
              <SelectItem key={type} value={type}>
                {t(TENANT_TYPE_LABEL_KEYS[type])}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Expiration Date */}
      <div className="space-y-2">
        <Label htmlFor="expiresAt">{t('tenantAdmin.expirationDate')}</Label>
        <Input
          id="expiresAt"
          type="date"
          {...expiresAtRegistration}
          ref={(element) => {
            expiresAtRegistration.ref(element);
            expiresAtElement.current = element;
          }}
          className={cn(errors.expiresAt && 'border-destructive')}
        />
        {errors.expiresAt && <p className="text-sm text-destructive">{errors.expiresAt.message}</p>}
        <p className="text-sm text-muted-foreground">{t('tenantAdmin.expirationHelper')}</p>
      </div>

      {/* Description ("Anmerkung") */}
      <div className="space-y-2 md:col-span-2">
        <Label htmlFor="description">{t('tenantAdmin.tenantDescription')}</Label>
        <Textarea
          id="description"
          rows={2}
          placeholder={t('tenantAdmin.tenantDescriptionPlaceholder')}
          {...register('description')}
        />
      </div>

      {/* One field for every tenant type: the id names the CUSTOMER whose
          account this tenant belongs to, and that account is what licence
          governance keys on. A partner's own firm IS that customer. */}
      {showHubspotLink && (
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="hubspotCompanyId">{t('tenantAdmin.hubspotCompanyId')}</Label>
          <Input
            id="hubspotCompanyId"
            type="text"
            placeholder={t('tenantAdmin.hubspotCompanyIdPlaceholder')}
            {...register('hubspotCompanyLinkId')}
          />
          <p className="text-sm text-muted-foreground">
            {tenantType === 'partner'
              ? t('tenantAdmin.hubspotPartnerCompanyIdHelper')
              : t('tenantAdmin.hubspotCompanyIdHelper')}
          </p>
        </div>
      )}
    </div>
  );
}
